import { HttpClient, HttpHeaders, HttpRequest, HttpEventType } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { api } from "src/environments/environment";
import { LoaderService } from "./loader.service";
import { Observable, tap, lastValueFrom } from "rxjs";
import { catchError } from "rxjs/operators";
import { Archivo } from "../interfaces/archivo.interface";
import { MimeType } from "./mimeType.service";
import * as fileSaver from 'file-saver';
import { MessageService } from 'primeng/api';

interface PresignUrlResponse {
    url: string;
    method: string;
    objectKey: string;
    expiresInSeconds: number;
    requiredHeaders?: { [key: string]: string };
}

@Injectable({
    providedIn: 'root'
})
export class ArchivoService {

    URL = `${api.urlAPI}` + 'api/adm/archivo/';
    URLARCHIVO = `${api.urlArchivo}` + 'ediscovery/';

    constructor(
        private http: HttpClient,
        private loaderService: LoaderService,
        private mimeType: MimeType,
        private messageService: MessageService,) { }

    async uploadFileInterno(file: File, cveElemento: string): Promise<void> {
        this.loaderService.isLoading.next(true);
        const formdata: FormData = new FormData();
        formdata.append('file', file);
        let headers = new HttpHeaders();
        headers = headers.set('Access-Control-Allow-Origin', '*');

        const req = new HttpRequest('POST', `${this.URLARCHIVO}upload/` + cveElemento, formdata, {
            headers: headers,
            reportProgress: true,
            responseType: 'text',
        });

        try {
            await lastValueFrom(
                this.http.request(req).pipe(
                    tap((event) => {
                        if (event.type === HttpEventType.UploadProgress) {
                            const progress = Math.round((event.loaded / (event.total || 1)) * 100);
                            console.log(`Upload Progress: ${progress}%`);
                        }
                    }),
                    catchError((error) => {
                        console.error('Error al cargar el archivo:', error);
                        throw error;
                    })
                )
            );
        } catch (err) {
            this.loaderService.isLoading.next(false);
            this.messageService.add({ severity: 'error', summary: 'Error!', detail: `Archivo: ${file.name} no cargado.`, life: 6000 });
        } finally {
            this.loaderService.isLoading.next(false);
            this.messageService.add({ severity: 'success', summary: 'Éxito', detail: `Archivo: ${file.name} cargado.`, life: 6000 });
        }
    }

    async uploadFileExterno(file: File, path: string) {
        try {
            const payload = {
                path,
                fileName: file.name,
                contentType: file.type || 'application/octet-stream'
            };

            const presign = await lastValueFrom(
                this.http.post<PresignUrlResponse>(`${this.URLARCHIVO}presign/upload-url`, payload)
            );

            let uploadHeaders = new HttpHeaders();
            if (presign.requiredHeaders) {
                Object.keys(presign.requiredHeaders).forEach((headerName) => {
                    const lowerHeaderName = headerName.toLowerCase();
                    if (lowerHeaderName !== 'host' && lowerHeaderName !== 'content-length') {
                        uploadHeaders = uploadHeaders.set(headerName, presign.requiredHeaders![headerName]);
                    }
                });
            }

            if (file.type && !uploadHeaders.has('content-type') && !uploadHeaders.has('Content-Type')) {
                uploadHeaders = uploadHeaders.set('Content-Type', file.type);
            }

            const uploadRequest = new HttpRequest('PUT', presign.url, file, {
                headers: uploadHeaders,
                reportProgress: true,
                responseType: 'text'
            });

            await lastValueFrom(
                this.http.request(uploadRequest).pipe(
                    tap((event) => {
                        if (event.type === HttpEventType.UploadProgress) {
                            const percentage = Math.round((event.loaded / (event.total || 1)) * 100);
                            const message = `Cargando archivo: ${file.name} - ${percentage}%`;
                            this.loaderService.isUploading.next({ status: true, message });
                        }
                    }),
                    catchError((error) => {
                        throw error;
                    })
                )
            );

            this.loaderService.isUploading.next({ status: false, message: '' });
            this.messageService.add({ severity: 'success', summary: 'Éxito', detail: `Archivo: ${file.name} cargado.`, life: 6000 });
        } catch (error) {
            this.loaderService.isUploading.next({ status: false, message: '' });
            this.messageService.add({ severity: 'error', summary: 'Error!', detail: `Archivo: ${file.name} no cargado.`, life: 6000 });
        }
    }

    saveFile(archivo: Archivo) {
        return this.http.post(this.URL + 'externos', archivo);
    }

    listFiles(id: string, origen: string): Observable<Archivo[]> {
        return this.http.get<Archivo[]>(this.URL + 'list/' + id + '/origen/' + origen);
    }

    updateVisible(id: string, cveSol: string, visible: boolean): Observable<any> {
        return this.http.post(this.URL + 'visible/id/' + id + '/cveSol/' + cveSol + '/visible/' + visible, null);
    }

    listFilesProyectoVisible(id: string): Observable<Archivo[]> {
        return this.http.get<Archivo[]>(this.URL + 'list/' + id);
    }

    listFilesSolVisible(id: string): Observable<Archivo[]> {
        return this.http.get<Archivo[]>(this.URL + 'listDocSolPro/' + id);
    }

    async downloadFile(archivo: string, path: string) {
        this.loaderService.isLoading.next(true);
        const normalizedPath = (path || '').replace(/\//g, '-');
        const tokenHeader = localStorage.getItem('tokenHeader') || '';

        const mime = this.mimeType.getMimeTypefromString('.' + this.getFileExtension3(archivo));

        this.http.get(this.URLARCHIVO + 'download/' + archivo + '/' + normalizedPath, {
            headers: new HttpHeaders({ tokenHeader }),
            responseType: 'blob',
            reportProgress: true,
            observe: 'events'
        }).subscribe({
            next: (event: any) => {
                if (event.type === HttpEventType.DownloadProgress) {
                    const porcentaje = Math.round((event.loaded / (event.total || 1)) * 100);
                    console.log(`Progreso de descarga: ${porcentaje}%`);
                } else if (event.type === HttpEventType.Response) {
                    const blob = new Blob([event.body], { type: mime });
                    fileSaver.saveAs(blob, archivo);
                    this.loaderService.isLoading.next(false);
                    this.messageService.add({ severity: 'success', summary: 'Éxito', detail: `Archivo: ${archivo} descargado.`, life: 6000 });
                }
            },
            error: (err) => {
                console.error('Error al descargar el archivo:', err);
                this.loaderService.isLoading.next(false);
                this.messageService.add({ severity: 'error', summary: 'Error!', detail: `Archivo: ${archivo} no descargado.`, life: 6000 });
            }
        });
    }

    async downloadExternal(path: string, archivo: string) {
        await this.downloadFile(archivo, path);
    }

    getFileExtension3(filename) {
        return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
    }

    eliminarArchivo(consecutivo) {
        return this.http.delete(this.URL + consecutivo);
    }

    eliminarArchivoProyecto(cveDoc) {
        return this.http.delete(this.URL + 'docProyecto/' + cveDoc);
    }
}